import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuthToken, removeAuthToken, removeUserId } from '../utils/cookies';
import { getCacheManager } from '../utils/cache-manager';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);

    const checkAuth = async () => {
        try {
            // 1. Quick check for JS-accessible cookies
            const token = getAuthToken();
            
            // Note: Even if we have cookies, we still want to fetch the profile 
            // to get user details and verify the session is still active.
            
            const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';
            const headers = token ? { Authorization: `Bearer ${token}` } : {};
            
            const res = await fetch(`${apiBaseUrl}/auth/profile`, {
                headers,
                credentials: 'include',
            });

            if (res.ok) {
                const data = await res.json();
                setUser(data);
                setIsAuthenticated(true);
            } else {
                setUser(null);
                setIsAuthenticated(false);
            }
        } catch (error) {
            console.error('Auth check failed:', error);
            setUser(null);
            setIsAuthenticated(false);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        checkAuth();
    }, []);

    const logout = async ({ notifyServer = true } = {}) => {
        const token = getAuthToken();
        const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8081';

        if (notifyServer) {
            fetch(`${apiBaseUrl}/auth/logout`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                credentials: 'include',
                keepalive: true,
            }).catch((error) => {
                // Server failure must not delay or prevent local logout.
                console.error('Server logout failed:', error);
            });
        }

        removeAuthToken();
        removeUserId();

        try {
            await getCacheManager().clearAll();
        } catch (error) {
            console.error('Client session cleanup failed:', error);
        } finally {
            setUser(null);
            setIsAuthenticated(false);
        }
    };

    return (
        <AuthContext.Provider value={{ user, isAuthenticated, loading, checkAuth, logout, setUser }}>
            {children}
        </AuthContext.Provider>
    );
};
